import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
	Badge, IconButton, TextField, Select, FormControl,
	InputLabel, Button, Box, Dialog, DialogContent, DialogActions
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { styled } from '@mui/material/styles';
import ClearIcon from '@mui/icons-material/Clear';
import { useLanguage } from 'Context/LanguageContext';
import {
	columnDataAvancedFilter,
	operationDataAvancedFilter,
	operatorDataAvancedFilter,
	operatorCategoryDataAvancedFilter,
	taskStatusDataAvancedFilter,
	taskGroupStatusDataAvancedFilter,
	typeOfPauseTaskAdvancedFilter,
} from 'OldComponents/AdvancedFilter/AvancedFilterUtil';
import { getDefaultPrioritiesLabel } from 'FielderUtils/Constants/TaskPriorityConstants';
import { defaultTypeTasks } from 'OldComponents/Analitics/AnaliticsUtil';
import AdvancedFilterUtilColum from 'OldComponents/AdvancedFilter/AdvancedFilterUtilColum';
import AdvancedFilterUtilOperator from 'OldComponents/AdvancedFilter/AdvancedFilterUtilOperator';
import { TreeSelect } from 'antd';
import { Cookie, FilterAlt } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Clear';
import Tooltip from '@mui/material/Tooltip';
import StarRatings from 'react-star-ratings';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { getCookiesRequest, updateCookiesRequest } from '@core/api/cookie/cookie.service';
import { getObjectCookie } from 'FielderUtils/cookie/cookieUtil';
import { FielderHard } from '@core/theme/baseTheme';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { AdaptAdvancedFilters } from '~/FielderElements/SmartTaskTable/SmartTableUtils';
import Cookies from 'universal-cookie';
import { useFielderSnackBar } from '~/FielderElements/useFielderSnackBar';

const StyledBadge = styled(Badge)(() => ({
	'& .MuiBadge-badge': { backgroundColor: '#dc5c87', color: 'white' },
}));
// ---------- helpers: default values per column ----------
const defaultValueForColumn = (col) => {
	if (
		col === AdvancedFilterUtilColum.STATUS ||
		col === AdvancedFilterUtilColum.STATUS_GROUP ||
		col === AdvancedFilterUtilColum.SCORE ||
		col === AdvancedFilterUtilColum.TYPE_PAUSE_REASON
	) return 0;
	if (
		col === AdvancedFilterUtilColum.PRIORITY ||
		col === AdvancedFilterUtilColum.TYPE_OF_TASK
	) return 1;
	return '';
};
const getFirstAvailableColumn = (allColumns, currentFilters) => {
	const used = new Set(currentFilters.map((f) => f.column));
	const candidate = allColumns.find((c) => !c.disabled && !used.has(c.value));
	return candidate ? candidate.value : '';
};
let idCounter = 0;
// ---------- helpers: merge/dedupe (saved + ad-hoc) ----------
const flatToNestedByColumn = (flat = []) => {
	// Accepts legacy keys (columSelected/valueField) or new (column/value)
	const normalize = (row) => ({
		operator: row.operator,
		column: row.column ?? row.columSelected,
		operation: row.operation,
		value: row.value ?? row.valueField,
		isParent: row.isParent !== false,
		parent: row.parent ?? (row.column ?? row.columSelected),
	});
	const map = new Map(); // column -> {operator,column,operation,value,subFilters:[]}
	let currentParentByCol = new Map();
	flat.forEach((raw) => {
		const row = normalize(raw);
		const col = row.parent ?? row.column;
		if (row.isParent) {
			if (!map.has(col)) {
				map.set(col, {
					operator: row.operator,
					column: col,
					operation: row.operation,
					value: row.value,
					subFilters: [],
				});
			} else map.get(col);
			currentParentByCol.set(col, map.get(col));
		} else {
			if (!map.has(col)) {
				map.set(col, {
					operator: row.operator,
					column: col,
					operation: row.operation,
					value: row.value,
					subFilters: [],
				});
			}
			const entry = currentParentByCol.get(col) ?? map.get(col);
			entry.subFilters.push({
				operator: row.operator,
				column: col,
				operation: row.operation,
				value: row.value,
			});
		}
	});
	return Array.from(map.values());
};
const dedupeSubFilters = (subs = []) => {
	const seen = new Set();
	const out = [];
	for (const s of subs) {
		const key = `${s.operation}::${String(s.value)}`;
		if (!seen.has(key)) { seen.add(key); out.push(s); }
	}
	return out;
};
const mergeNestedGroups = (...groupsArrays) => {
	const byCol = new Map(); // col -> {operator,column,operation,value,subFilters}
	for (const arr of groupsArrays) {
		for (const g of arr) {
			const col = g.column;
			if (!byCol.has(col)) {
				byCol.set(col, { ...g, subFilters: [...(g.subFilters || [])] });
			} else {
				const prev = byCol.get(col);
				// keep the earliest parent's operator/value; merge/dedupe subfilters
				prev.subFilters = dedupeSubFilters([...(prev.subFilters || []), ...(g.subFilters || [])]);
			}
		}
	}
	return Array.from(byCol.values());
};
const nestedToFlat = (groups = []) => {
	const out = [];
	groups.forEach((g) => {
		const count = 1 + (g.subFilters?.length || 0);
		out.push({
			operator: g.operator,
			column: g.column,
			operation: g.operation,
			value: g.value,
			isParent: true,
			parent: g.column,
			countCategory: count,
		});
		(g.subFilters || []).forEach((sf) => {
			out.push({
				operator: sf.operator,
				column: g.column,
				operation: sf.operation,
				value: sf.value,
				isParent: false,
				parent: g.column,
				countCategory: count,
			});
		});
	});
	return out;
};
const AdvancedFilter = (
	{
		executeAdvancedFilters = () => {},
		disabled,
		showApplyButton = true,
		value = [],
		clearSignal,
		typeOfTasks,
		priorityOfTasks,
	}) => {
	const tableCookie = {
		tableCookieName: 'MyOperationTaskTableAvancedFilter', cookie: new Cookies()
	};
	const lan = useLanguage();
	const fielderMsg = useFielderSnackBar();
	// ---------- local editor (ad-hoc) filters (nested shape) ----------
	const [filters, setFilters] = useState(value || []);
	const clearSignalRef = useRef(clearSignal);
	// catalogs / options
	const columns = columnDataAvancedFilter(lan);
	const operations = operationDataAvancedFilter(lan);
	const operators = operatorDataAvancedFilter(lan);
	const operatorCategories = operatorCategoryDataAvancedFilter(lan);
	const taskTypes = useMemo(
		() =>
			Array.isArray(typeOfTasks) && typeOfTasks.length > 0
				? typeOfTasks
				: defaultTypeTasks(lan),
		[lan, typeOfTasks],
	);
	const priorityOptions = useMemo(
		() =>
			Array.isArray(priorityOfTasks) && priorityOfTasks.length > 0
				? priorityOfTasks
				: getDefaultPrioritiesLabel(lan),
		[lan, priorityOfTasks],
	);
	const statusOptions = taskStatusDataAvancedFilter(lan);
	const groupStatusOptions = taskGroupStatusDataAvancedFilter(lan);
	const pauseTypeOptions = typeOfPauseTaskAdvancedFilter(lan);
	const [openModal, setOpenModal] = useState(false);
	// value renderer
	const renderValueInput = (column, value, onChange) => {
		switch (column) {
			case AdvancedFilterUtilColum.ID_TASK:
				return (
					<TextField
						id="txt-filter-input"
						fullWidth
						size="small"
						value={value}
						onChange={(e) => {
							const val = e.target.value;
							if (/^\d*$/.test(val)) onChange(val === '' ? '' : Number(val));
						}}
						slotProps={{
							input: {
								inputMode: 'numeric',
								pattern: '[0-9]*',
							},
						}}
						label={lan.value}
					/>
				);
			case AdvancedFilterUtilColum.PRIORITY:
				return (
					<FormControl fullWidth size="small">
						<InputLabel>{lan.priority}</InputLabel>
						<Select
							native
							id="select-filter-priority"
							value={value} label={lan.priority}
							onChange={(e) => onChange(e.target.value)}>
							{priorityOptions.map((opt, idx) => (
								<option  key={idx} value={opt.value ?? idx}>
									{lan[opt.label] || opt.label}
								</option >
							))}
						</Select>
					</FormControl>
				);
			case AdvancedFilterUtilColum.TYPE_OF_TASK:
				return (
					<TreeSelect
						style={{ width: '100%' }}
						treeData={taskTypes}
						value={typeof value === 'number' ? value : undefined}
						dropdownStyle={{ maxHeight: 400, overflow: 'auto', zIndex: 10000, minWidth: '300px', whiteSpace: 'nowrap' }}
						placeholder={lan.taskType}
						onChange={(val) => onChange(parseInt(val))}
					/>
				);
			case AdvancedFilterUtilColum.STATUS:
				return (
					<FormControl fullWidth size="small">
						<InputLabel>{lan.status}</InputLabel>
						<Select
							native
							id="select-filter-status"
							value={value} label={lan.status}
							onChange={(e) => onChange(e.target.value)}>
							{statusOptions.map((opt, idx) => (
								<option key={idx} value={opt.value ?? idx}>
									{lan[opt.label] || opt.label}
								</option>
							))}
						</Select>
					</FormControl>
				);
			case AdvancedFilterUtilColum.STATUS_GROUP:
				return (
					<FormControl fullWidth size="small">
						<InputLabel>{lan.statusGroup}</InputLabel>
						<Select
							native
							id="select-filter-status-group"
							value={value} label={lan.statusGroup}
							onChange={(e) => onChange(e.target.value)}>
							{groupStatusOptions.map((opt, idx) => (
								<option key={idx} value={opt.value ?? idx}>
									{lan[opt.label] || opt.label}
								</option>
							))}
						</Select>
					</FormControl>
				);
			case AdvancedFilterUtilColum.TYPE_PAUSE_REASON:
				return (
					<FormControl fullWidth size="small">
						<InputLabel>{lan.typePauseReason}</InputLabel>
						<Select
							native
							id="taskType-red-input"
							value={value} label={lan.typePauseReason}
							onChange={(e) => onChange(e.target.value)}>
							{pauseTypeOptions.map((opt, idx) => (
								<option key={idx} value={opt.value ?? idx}>
									{lan[opt.label] || opt.label}
								</option>
							))}
						</Select>
					</FormControl>
				);
			case AdvancedFilterUtilColum.SCORE:
				return (
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<StarRatings
							rating={Number(value) || 0}
							numberOfStars={5}
							changeRating={(newRating) => onChange(Number(newRating))}
							starDimension="18px"
							starSpacing="2px"
							starRatedColor="yellow"
							starHoverColor="yellow"
						/>
					</Box>
				);
			default:
				return (
					<TextField
						id="txt-filter-input"
						fullWidth
						size="small"
						value={value}
						onChange={(e) => onChange(e.target.value)}
						label={lan.value}
					/>
				);
		}
	};
	// ---------- saved filters (cookies) ----------
	const [savedFilters, setSavedFilters] = useState([]); // [{name, filter:[nested rows]}]
	const [filterName, setFilterName] = useState('');
	const [activeSaved, setActiveSaved] = useState(new Set()); // indexes of active chips
	// normalize incoming prop value (flat or nested) into nested editor state
	useEffect(() => () => { idCounter = 0; }, []);
	useEffect(() => {
		const toNested = (vals) => {
			if (!Array.isArray(vals)) return [];
			const result = [];
			let currentParent = null;
			vals.forEach((it) => {
				// Support both legacy (columSelected/valueField) and new (column/value)
				const isParent = it.isParent !== false || it.subFilters;
				if (isParent) {
					const column = it.column ?? it.columSelected;
					const value = it.value ?? it.valueField;
					const parent = {
						id: idCounter++,
						operator: it.operator,
						column,
						operation: it.operation,
						value,
						subFilters: [],
					};
					result.push(parent);
					currentParent = parent;
					if (Array.isArray(it.subFilters)) {
						it.subFilters.forEach((sf) => {
							currentParent.subFilters.push({
								id: idCounter++,
								operator: sf.operator,
								column,
								operation: sf.operation,
								value: sf.value ?? sf.valueField,
							});
						});
					}
				} else if (currentParent) {
					currentParent.subFilters.push({
						id: idCounter++,
						operator: it.operator,
						column: currentParent.column,
						operation: it.operation,
						value: it.value ?? it.valueField,
					});
				} else {
					const column = it.column ?? it.columSelected;
					const value = it.value ?? it.valueField;
					result.push({
						id: idCounter++,
						operator: it.operator,
						column,
						operation: it.operation,
						value,
						subFilters: [],
					});
				}
			});
			return result;
		};
		idCounter = 0;
		setFilters(toNested(value));
	}, [value]);
	useEffect(() => {
		if (clearSignalRef.current === clearSignal) return;
		clearSignalRef.current = clearSignal;
		setFilters([]);
		idCounter = 0;
	}, [clearSignal]);
	const handleClick = () => {
		if (disabled) return;
		loadCookieAdvancedFilters();
		setOpenModal(true);
	};
	const handleClose = () => setOpenModal(false);
	useEffect(() => { if (disabled) handleClose(); }, [disabled]);
	// editor actions
	const addFilter = () => {
		setFilters((prev) => {
			const chosenCol = getFirstAvailableColumn(columns, prev);
			if (chosenCol === '') return prev; // nothing left to add
			return [
				...prev,
				{
					id: idCounter++,
					operator:
						prev.length === 0
							? AdvancedFilterUtilOperator.WHERE
							: operators[0]?.value ?? AdvancedFilterUtilOperator.AND,
					column: chosenCol,
					operation: operations[0]?.value ?? 0,
					value: defaultValueForColumn(chosenCol),
					subFilters: [],
				},
			];
		});
	};
	const addSubFilter = (parentId) => {
		setFilters((prev) =>
			prev.map((f) =>
				f.id === parentId
					? {
						...f,
						subFilters: [
							...(f.subFilters || []),
							{
								id: idCounter++,
								operator: operatorCategories[0]?.value ?? AdvancedFilterUtilOperator.OR,
								column: f.column,
								operation: operations[0]?.value ?? 0,
								value: defaultValueForColumn(f.column),
							},
						],
					}
					: f,
			),
		);
	};
	const removeSubFilter = (parentId, subId) => {
		setFilters((prev) =>
			prev.map((f) =>
				f.id === parentId
					? { ...f, subFilters: (f.subFilters || []).filter((sf) => sf.id !== subId) }
					: f,
			),
		);
	};
	const updateSubFilter = (parentId, subId, key, value) => {
		setFilters((prev) =>
			prev.map((f) =>
				f.id === parentId
					? {
						...f,
						subFilters: (f.subFilters || []).map((sf) =>
							sf.id === subId
								? {
									...sf,
									[key]: value,
									...(key === 'column' && sf.column !== value
										? { value: defaultValueForColumn(value) }
										: {}),
								}
								: sf,
						),
					}
					: f,
			),
		);
	};
	const removeFilter = (id) => setFilters((prev) => prev.filter((f) => f.id !== id));
	const updateFilter = (id, key, val) => {
		setFilters((prev) =>
			prev.map((f) => {
				if (f.id !== id) return f;
				if (key === 'column') {
					const newCol = val;
					return { ...f, column: newCol, value: defaultValueForColumn(newCol) };
				}
				return { ...f, [key]: val };
			}),
		);
	};
	// flatten the current editor filters (for saving)
	const flattenCurrentFilters = (nested) => {
		const out = [];
		nested.forEach((f) => {
			const { subFilters = [], ...rest } = f;
			out.push({ ...rest, isParent: true, parent: f.column });
			subFilters.forEach((sf) => {
				out.push({ ...sf, column: f.column, isParent: false, parent: f.column });
			});
		});
		return out;
	};
	// cookies: load
	const loadCookieAdvancedFilters = () => {
		const body = { filterCookie: { cookieName: tableCookie.tableCookieName } };
		const onSuccess = (resp) => {
			const list = resp?.avancedFiltersCookie;
			localStorage.setItem(tableCookie.tableCookieName, JSON.stringify(list ?? []));
			if (Array.isArray(list) && list.length > 0) {
				const nested = list.map((sf) => ({
					...sf,
					filter: flatToNestedByColumn(sf.filter),
				}));
				setSavedFilters(nested);
			} else setSavedFilters([]);
		};
		getCookiesRequest(body, onSuccess, null, null);
	};
	// cookies: save
	const saveCookieAdvancedFilters = () => {
		const flat = AdaptAdvancedFilters(filters);
		if (!flat.length) return;
		const raw = localStorage.getItem(tableCookie.tableCookieName);
		const current = raw ? JSON.parse(raw) : [];
		const name =
			(filterName && filterName.trim()) ||
			`${lan.filter}${(current?.length || 0) + 1}`;
		const next = [...current, { name, filter: flat }];
		localStorage.setItem(tableCookie.tableCookieName, JSON.stringify(next));
		const body = getObjectCookie(tableCookie.tableCookieName, next);
		updateCookiesRequest(body).then(r => {
			if (r.success) fielderMsg({
				title: lan.myOperation, message: lan.advancedFilterSavedSuccessfully,
				variant: 'success', closeButton: true, duration: 3000, });
		});
		const nested = next.map((sf) => ({
			...sf,
			filter: flatToNestedByColumn(sf.filter),
		}));
		setSavedFilters(nested);
		setFilterName('');
	};
	// cookies: remove
	const removeSavedFilter = (index) => {
		const raw = localStorage.getItem(tableCookie.tableCookieName);
		const list = raw ? JSON.parse(raw) : [];
		list.splice(index, 1);
		localStorage.setItem(tableCookie.tableCookieName, JSON.stringify(list));
		const body = getObjectCookie(tableCookie.tableCookieName, list);
		updateCookiesRequest(body).then(r => {
			console.log(r);
		});
		const nested = list.map((sf) => ({
			...sf,
			filter: flatToNestedByColumn(sf.filter),
		}));
		setSavedFilters(nested);

		setActiveSaved((prev) => {
			const next = new Set(prev);
			next.delete(index);
			return next;
		});
	};
	const selectSavedChip = (index) => {
		setActiveSaved((prev) => {
			const next = new Set();
			if (!prev.has(index)) {
				next.add(index);
				const selected = savedFilters?.[index]?.filter || [];
				setFilters(selected);
			} else {
				setFilters([]);
			}
			return next;
		});
	};
	// get translated column label
	const getColumnLabel = (colVal) => {
		const col = columns.find((c) => c.value === colVal);
		return col ? (lan[col.label] || col.label) : '';
	};
	// APPLY: merge editor filters + all active saved chips, then execute
	const applyFilters = () => {
		const editorNested = filters.map((f) => ({
			operator: f.operator,
			column: f.column,
			operation: f.operation,
			value: f.value,
			subFilters: (f.subFilters || []).map((sf) => ({
				operator: sf.operator,
				column: f.column,
				operation: sf.operation,
				value: sf.value,
			})),
		}));
		const activeSavedNested = [];
		activeSaved.forEach((idx) => {
			activeSavedNested.push(savedFilters[idx]?.filter || []);
		});
		const merged = mergeNestedGroups(...activeSavedNested, editorNested);
		const flatToExecute = nestedToFlat(merged);
		executeAdvancedFilters(flatToExecute);
		handleClose();
	};
	return (
		<>
			<StyledBadge badgeContent={disabled ? 0 : filters.length} invisible={disabled}>
				<Tooltip title={lan.advancedFilter || 'Advanced filter'}>
					<IconButton onClick={handleClick} disabled={disabled} size="small">
						<FilterAlt />
					</IconButton>
				</Tooltip>
			</StyledBadge>
			<Dialog
				open={openModal}
				onClose={handleClose}
				maxWidth={false}
				slotProps={{
					paper: {
						sx: {
							position: 'relative', overflow: 'visible', borderRadius: 2, minWidth: 600, maxWidth: '90vw', width: 'auto'
						},
					},
				}}
			>
				<IconButton
					onClick={handleClose}
					size="small"
					sx={{
						position: 'absolute', top: -8, right: -8, width: 24, height: 24, bgcolor: '#fff',
						border: '0px solid #dc5c87', zIndex: 10, boxShadow: 2, p: 0,
						'&:hover': { bgcolor: '#dc5c87' },
					}}
				>
					<CloseIcon
						fontSize="small"
						sx={{ color: '#dc5c87', '&:hover': { color: '#fff' } }}
					/>
				</IconButton>

				<DialogContent dividers>
					{savedFilters.length > 0 && (
						<Box sx={{ mb: 2 }}>
							<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
								{savedFilters.map((sf, i) => {
									const isActive = activeSaved.has(i);
									return (
										<Chip
											key={`sf-${i}`}
											label={sf.name}
											size={'small'}
											variant={isActive ? 'filled' : 'outlined'}
											onClick={(e) => {
												e.stopPropagation();
												selectSavedChip(i);
											}}
											onDelete={() => removeSavedFilter(i)}
											deleteIcon={<ClearIcon fontSize="small" />}
											sx={{
												bgcolor: isActive ? '#dc5c87' : 'transparent',
												borderColor: '#dc5c87',
												color: isActive ? '#fff' : '#dc5c87',
												'& .MuiChip-label': { color: 'inherit' },
												'& .MuiChip-deleteIcon': { color: isActive ? 'rgba(255,255,255,0.9)' : '#dc5c87' },
												'&:hover': {
													bgcolor: isActive ? FielderHard : 'rgba(220,92,135,0.06)',
													borderColor: '#b84b6e',
												},
												cursor: 'pointer',
											}}
											icon={<Cookie />}
										/>
									);
								})}
							</Stack>
						</Box>
					)}
					{/* Editor area */}
					<Grid container direction="column" spacing={1}>
						{filters.length === 0 && (
							<Grid>
								<Box sx={{ p: 2, textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
									{lan.userFilterTo}
								</Box>
							</Grid>
						)}
						{filters.map((f, index) => (
							<Grid key={f.id}>
								<Box sx={{ border: '1px solid #444', borderRadius: 2, p: 2, mb: 0 }}>
									<Grid container spacing={0.5} alignItems="center">
										<Grid xs={1.5}>
											{index === 0 ? (
												<span>{lan.where}</span>
											) : (
												<FormControl fullWidth size="small">
													<InputLabel>{lan.operator}</InputLabel>
													<Select
														native
														id="select-filter-operator"
														value={f.operator}
														label={lan.operator}
														onChange={(e) => updateFilter(f.id, 'operator', e.target.value)}
													>
														{operatorDataAvancedFilter(lan).map((op, idx) => (
															<option key={idx} value={op.value ?? idx}>
																{lan[op.label] || op.label}
															</option>
														))}
													</Select>
												</FormControl>
											)}
										</Grid>
										{/* Legacy-compatible main column <select> (prevents duplicate parent columns) */}
										<Grid xs={2.5}>
											<select
												key={`select-colum-${f.id}`}
												value={f.column}
												className="custom-select"
												id="taskType-red-input"
												onChange={(e) => updateFilter(f.id, 'column', parseInt(e.target.value))}
											>
												{columns.map((c) => {
													const isUsed = filters.some((other) => other.id !== f.id && other.column === c.value);
													return (
														<option key={c.value} value={c.value} disabled={c.disabled || isUsed}>
															{lan[c.label] || c.label}
														</option>
													);
												})}
											</select>
										</Grid>
										<Grid xs={1.5}>
											<FormControl fullWidth size="small">
												<InputLabel>{lan.operation}</InputLabel>
												<Select
													native
													id="select-filter-operation"
													value={f.operation}
													label={lan.operation}
													onChange={(e) => updateFilter(f.id, 'operation', e.target.value)}
												>
													{operations.map((op, idx) => (
														<option key={idx} value={op.value ?? idx}>
															{lan[op.label] || op.label}
														</option>
													))}
												</Select>
											</FormControl>
										</Grid>
										<Grid xs={2.5}>
											{renderValueInput(
												f.column,
												f.value,
												(val) => updateFilter(f.id, 'value', val),
											)}
										</Grid>
										<Grid xs={1}>
											<Box sx={{ display: 'flex', gap: 1 }}>
												<IconButton size="small" onClick={() => addSubFilter(f.id)}>
													<AddCircleIcon />
												</IconButton>
												<IconButton size="small" onClick={() => removeFilter(f.id)}>
													<ClearIcon fontSize="small" />
												</IconButton>
											</Box>
										</Grid>
									</Grid>
									{/* subfilters */}
									{f.subFilters?.map((sf) => (
										<Grid
											container
											key={sf.id}
											spacing={1}
											alignItems="center"
											sx={{ pl: 4, mt: 1 }}
										>
											<Grid xs={1.5}>
												<FormControl fullWidth size="small">
													<InputLabel>{lan.operator}</InputLabel>
													<Select
														native
														id="select-filter-sub-operator"
														value={sf.operator}
														label={lan.operator}
														onChange={(e) =>
															updateSubFilter(f.id, sf.id, 'operator', e.target.value)}
													>
														{operatorCategoryDataAvancedFilter(lan).map((op, idx) => (
															<option key={idx} value={op.value ?? idx}>
																{lan[op.label] || op.label}
															</option>
														))}
													</Select>
												</FormControl>
											</Grid>
											{/* read-only child column (kept for Katalon alignment) */}
											<Grid xs={2.5}>
												<select
													key={`select-colum-child-${sf.id}`}
													defaultValue={f.column}
													className="custom-select"
													id="taskType-red-input"
													disabled
												>
													<option value={f.column} disabled>
														{getColumnLabel(f.column)}
													</option>
												</select>
											</Grid>
											<Grid xs={2.5}>
												<FormControl fullWidth size="small">
													<InputLabel>{lan.operation}</InputLabel>
													<Select
														native
														id="select-filter-sub-operation"
														value={sf.operation}
														label={lan.operation}
														onChange={(e) =>
															updateSubFilter(f.id, sf.id, 'operation', e.target.value)}
													>
														{operations.map((op, idx) => (
															<option key={idx} value={op.value ?? idx}>
																{lan[op.label] || op.label}
															</option>
														))}
													</Select>
												</FormControl>
											</Grid>
											<Grid xs={2.5}>
												{renderValueInput(
													f.column,
													sf.value,
													(val) => updateSubFilter(f.id, sf.id, 'value', val),
												)}
											</Grid>
											<Grid xs={1}>
												<IconButton size="small" onClick={() => removeSubFilter(f.id, sf.id)}>
													<ClearIcon fontSize="small" />
												</IconButton>
											</Grid>
										</Grid>
									))}
								</Box>
							</Grid>
						))}
					</Grid>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'space-between', px: 1, py: 1 }}>
					<Button
						onClick={addFilter}
						startIcon={<AddCircleIcon />}
						size="small"
						sx={{ color: '#dc5c87', border: '1px solid #dc5c87' }}
					>
						{lan.addFilter}
					</Button>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
						<TextField
							size="small"
							placeholder={lan.filterName}
							value={filterName}
							onChange={(e) => setFilterName(e.target.value)}
						/>
						<Button
							size="small"
							onClick={saveCookieAdvancedFilters}
							sx={{ color: '#dc5c87', border: '1px solid #dc5c87' }}
						>
							{lan.saveFilter}
						</Button>
						{showApplyButton && (
							<Button
								variant="contained"
								onClick={applyFilters}
								size="small"
								sx={{ backgroundColor: '#dc5c87' }}
							>
								{lan.apply}
							</Button>
						)}
					</Box>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default AdvancedFilter;
